import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { extractApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import {
  BookOpen, Compass, Sparkles, CheckCircle2, Clock, ChevronRight,
  ArrowRight, GraduationCap, Wrench, Users, Palette,
} from "lucide-react";

const PATHWAY_ICON = {
  pw_trades: Wrench,
  pw_admin: Users,
  pw_visual: Palette,
};

function progressFor(moduleIds, progress) {
  const done = (progress?.lesson_progress || []).filter(
    (p) => moduleIds.includes(p.module_id) && p.status === "completed",
  ).length;
  return { done, total: moduleIds.length };
}

function findCourse(courses, id) {
  return (courses || []).find((c) => c.id === id) || null;
}

function findNextIncomplete(course, progress) {
  if (!course?.modules) return null;
  const doneSet = new Set(
    (progress?.lesson_progress || [])
      .filter((p) => p.status === "completed")
      .map((p) => p.module_id),
  );
  const sorted = [...course.modules].sort((a, b) => a.order - b.order);
  return sorted.find((m) => !doneSet.has(m.id)) || null;
}

export default function Learning() {
  const nav = useNavigate();
  const [state, setState] = useState({ courses: [], details: {}, capstoneFields: [] });
  const [progress, setProgress] = useState(null);
  const [recommendation, setRecommendation] = useState(null);
  const [mapping, setMapping] = useState(null);        // NEW M-W7 Stage 2C
  const [mappingMissing, setMappingMissing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [enrollingId, setEnrollingId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [list, prog, rec] = await Promise.all([
          api.get("/courses"),
          api.get("/courses/progress"),
          api.get("/courses/recommendation"),
        ]);
        // Fetch deep detail for each course (modules list)
        const details = {};
        const deep = await Promise.all(
          list.data.courses.map((c) => api.get(`/courses/${c.id}`).then((r) => r.data.course)),
        );
        for (const c of deep) details[c.id] = c;
        setState({
          courses: list.data.courses,
          details,
          capstoneFields: list.data.capstone_fields || [],
        });
        setProgress(prog.data);
        setRecommendation(rec.data);
        // M-W7 Stage 2C: pull the evidence-based pathway mapping. Honest
        // "no confirmed direction yet" state when the participant hasn't
        // selected one.
        try {
          const mp = await api.get("/pathway-mapping/current");
          if (mp.data?.mapping) {
            setMapping(mp.data.mapping);
            setMappingMissing(false);
          } else {
            setMapping(null);
            setMappingMissing(true);
          }
        } catch {
          setMapping(null);
        }
      } catch (err) {
        setError(extractApiError(err, "Failed to load your learning workspace."));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const foundations = useMemo(() => state.details["foundations"] || null, [state]);
  const pathways = useMemo(
    () => state.courses.filter((c) => c.kind === "pathway").map((c) => state.details[c.id]).filter(Boolean),
    [state],
  );

  const activeEnrollment = (progress?.enrollments || []).find((e) => e.active) || null;

  const foundationsProgress = useMemo(() => {
    if (!foundations) return { done: 0, total: 0 };
    return progressFor(foundations.modules.map((m) => m.id), progress);
  }, [foundations, progress]);

  const nextInFoundations = foundations && findNextIncomplete(foundations, progress);
  const nextInActivePathway = activeEnrollment
    ? findNextIncomplete(state.details[activeEnrollment.path_id], progress)
    : null;

  const enroll = async (pathId, isSwitch) => {
    setEnrollingId(pathId);
    try {
      if (isSwitch) {
        await api.post("/courses/enrollments/switch", { path_id: pathId });
      } else {
        await api.post("/courses/enrollments", { path_id: pathId, confirmed: true });
      }
      const prog = await api.get("/courses/progress");
      setProgress(prog.data);
      toast.success(isSwitch ? "Switched pathway — your progress is preserved." : "Enrolled — welcome.");
    } catch (err) {
      toast.error(extractApiError(err, "Enrollment failed."));
    } finally {
      setEnrollingId(null);
    }
  };

  if (loading) {
    return (
      <AppShell>
        <div className="mx-auto max-w-3xl px-4 py-16 text-center text-fg-secondary" data-testid="learning-loading">
          Loading your learning workspace…
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="mb-6">
          <p className="text-xs uppercase tracking-wider text-fg-secondary">Practical AI skill practice</p>
          <h1 className="mt-1 text-3xl sm:text-4xl tracking-tight font-heading font-medium text-fg-primary" data-testid="learning-title">
            AI Work Foundations & Pathways
          </h1>
          <p className="mt-3 max-w-2xl text-sm text-fg-secondary">
            Short lessons and real exercises for using AI tools responsibly in
            everyday work. Nothing here is a guarantee of a job or income —
            it's practical practice, checklists, and templates you can reuse.
          </p>
        </div>

        {error && <Alert variant="destructive" className="mb-4"><AlertDescription data-testid="learning-error">{error}</AlertDescription></Alert>}

        {/* Recommended next action */}
        {(nextInFoundations || nextInActivePathway) && (
          <div className="mb-8 rounded-lg border border-accent-warm/40 bg-accent-warm/5 p-4 flex flex-wrap items-center justify-between gap-3" data-testid="next-action-banner">
            <div>
              <p className="text-xs uppercase tracking-wider text-accent-warm">One recommended next action</p>
              <p className="mt-1 text-sm text-fg-primary" data-testid="next-action-text">
                {(nextInActivePathway && activeEnrollment
                  ? `In your active pathway "${state.details[activeEnrollment.path_id]?.title}": `
                  : `In Foundations: `)}
                <span className="font-medium">{(nextInActivePathway || nextInFoundations).title}</span>
              </p>
            </div>
            <Link to={`/learning/module/${(nextInActivePathway || nextInFoundations).id}`}>
              <Button size="sm" data-testid="next-action-btn" className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover">
                Continue <ArrowRight className="ml-2 h-3.5 w-3.5" aria-hidden="true" />
              </Button>
            </Link>
          </div>
        )}

        {/* Foundations */}
        {foundations && (
          <section className="mb-10" data-testid="foundations-section">
            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <div>
                <h2 className="text-2xl font-heading font-medium text-fg-primary flex items-center gap-2">
                  <GraduationCap className="h-5 w-5" aria-hidden="true" />
                  AI Work Foundations
                </h2>
                <p className="mt-1 text-xs text-fg-secondary">Everyone starts here. Nine short modules.</p>
              </div>
              <p data-testid="foundations-progress" className="text-xs text-fg-secondary">
                {foundationsProgress.done} / {foundationsProgress.total} modules complete
              </p>
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              {foundations.modules.map((m) => (
                <ModuleRow key={m.id} m={m} progress={progress} testidPrefix="foundation" />
              ))}
            </div>
          </section>
        )}

        {/* Pathways */}
        <section className="mb-10" data-testid="pathways-section">
          <div className="flex items-baseline gap-2">
            <h2 className="text-2xl font-heading font-medium text-fg-primary flex items-center gap-2">
              <Compass className="h-5 w-5" aria-hidden="true" />
              Choose a pathway
            </h2>
          </div>
          <p className="mt-1 text-xs text-fg-secondary">
            {activeEnrollment
              ? `You're currently enrolled in "${state.details[activeEnrollment.path_id]?.title}". You can switch below without losing progress.`
              : "All three are available. Pick the one that feels closest to how you'd like to work — you can switch later without losing progress."}
          </p>

          {recommendation?.explanation && !mapping && (
            <div className="mt-3 rounded-md border border-progress-teal/30 bg-progress-teal/5 p-3" data-testid="recommendation-banner">
              <p className="text-[11px] uppercase tracking-wider text-progress-teal">Recommendation</p>
              <p data-testid="recommendation-explanation" className="mt-1 text-sm text-fg-primary">{recommendation.explanation}</p>
              {recommendation.recommended_pathway_id && (
                <p data-testid="recommendation-target" className="mt-1 text-xs text-fg-secondary">
                  Recommended pathway id: <code>{recommendation.recommended_pathway_id}</code>
                </p>
              )}
            </div>
          )}

          {/* M-W7 Stage 2C: evidence-based mapping ribbon */}
          {mapping && (
            <MappingRibbon
              mapping={mapping}
              pathways={pathways}
              activeEnrollment={activeEnrollment}
            />
          )}
          {mappingMissing && (
            <div data-testid="mapping-no-direction" className="mt-3 rounded-md border border-border-subtle bg-bg-surface p-3">
              <p className="text-xs text-fg-secondary">
                You haven't confirmed a direction yet — that's why there's no
                pathway match shown. All three pathways are available; pick any
                to explore, and confirming a direction later will surface an
                evidence-based match here.
              </p>
              <Link to="/results#directions">
                <Button size="sm" variant="ghost" data-testid="mapping-no-direction-btn" className="mt-2 text-fg-secondary hover:text-fg-primary">
                  Open Directions <ChevronRight className="ml-1 h-3 w-3" aria-hidden="true" />
                </Button>
              </Link>
            </div>
          )}

          <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
            {pathways.map((pw) => {
              const Icon = PATHWAY_ICON[pw.id] || Compass;
              const enrolled = (progress?.enrollments || []).find((e) => e.path_id === pw.id);
              const isActive = enrolled && enrolled.active;
              // M-W7 Stage 2C: prefer the new mapping's recommendation
              // over the legacy keyword one when we have it.
              const recommendedId = mapping?.recommended_pathway_id || recommendation?.recommended_pathway_id;
              const isRecommended = recommendedId === pw.id;
              const p = progressFor(pw.modules.map((m) => m.id), progress);
              return (
                <div
                  key={pw.id}
                  data-testid={`pathway-${pw.id}`}
                  data-active={isActive || false}
                  data-recommended={isRecommended || false}
                  className={
                    "rounded-lg border p-5 flex flex-col " +
                    (isActive
                      ? "border-accent-warm bg-bg-surface"
                      : isRecommended
                      ? "border-progress-teal/40 bg-bg-surface"
                      : "border-border-subtle bg-bg-surface")
                  }
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-accent-warm/10 text-accent-warm">
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </span>
                    {isActive && (
                      <span data-testid={`pathway-${pw.id}-active-badge`} className="text-[11px] uppercase tracking-wider px-2 py-1 rounded border border-accent-warm text-accent-warm bg-accent-warm/10">
                        Active
                      </span>
                    )}
                    {isRecommended && !isActive && (
                      <span data-testid={`pathway-${pw.id}-recommended-badge`} className="text-[11px] uppercase tracking-wider px-2 py-1 rounded border border-progress-teal/40 text-progress-teal bg-progress-teal/10">
                        Recommended
                      </span>
                    )}
                  </div>
                  <h3 className="mt-3 text-lg font-heading font-medium text-fg-primary" data-testid={`pathway-${pw.id}-title`}>
                    {pw.title}
                  </h3>
                  <p className="mt-1 text-sm text-fg-secondary flex-1" data-testid={`pathway-${pw.id}-description`}>
                    {pw.description}
                  </p>
                  <p className="mt-2 text-xs text-fg-secondary" data-testid={`pathway-${pw.id}-progress`}>
                    {p.done} / {p.total} lessons complete
                    {pw.capstone ? " · capstone available" : ""}
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {!isActive ? (
                      <Button
                        size="sm"
                        onClick={() => enroll(pw.id, !!activeEnrollment)}
                        disabled={enrollingId === pw.id}
                        data-testid={`pathway-${pw.id}-enroll-btn`}
                        className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
                      >
                        {enrollingId === pw.id ? "…" : (activeEnrollment ? "Switch to this" : "Enroll")}
                      </Button>
                    ) : (
                      <span data-testid={`pathway-${pw.id}-enrolled-copy`} className="text-xs text-fg-secondary italic">
                        Enrolled. Open a lesson to continue.
                      </span>
                    )}
                    <Link to={`/learning/pathway/${pw.id}`}>
                      <Button size="sm" variant="ghost" data-testid={`pathway-${pw.id}-view-btn`} className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover">
                        View lessons <ChevronRight className="ml-1 h-3 w-3" aria-hidden="true" />
                      </Button>
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Capstone projects section */}
        {activeEnrollment && state.details[activeEnrollment.path_id]?.capstone && (
          <CapstoneEntry course={state.details[activeEnrollment.path_id]} progress={progress} />
        )}
      </div>
    </AppShell>
  );
}

function ModuleRow({ m, progress, testidPrefix }) {
  const p = (progress?.lesson_progress || []).find((x) => x.module_id === m.id);
  const status = p?.status || "not_started";
  const StatusIcon = status === "completed" ? CheckCircle2 : Clock;
  const statusClass = status === "completed"
    ? "text-progress-teal"
    : status === "in_progress"
    ? "text-accent-warm"
    : "text-fg-muted";
  return (
    <Link
      to={`/learning/module/${m.id}`}
      data-testid={`${testidPrefix}-module-${m.id}`}
      data-status={status}
      className="rounded-lg border border-border-subtle bg-bg-surface p-4 flex items-center justify-between hover:border-accent-warm/40 transition-colors"
    >
      <div>
        <p className="text-xs text-fg-secondary">
          {m.reading_time_min ? `${m.reading_time_min} min · ` : ""}
          <span className={statusClass}>
            {status === "completed" ? "Completed" : status === "in_progress" ? "In progress" : "Not started"}
          </span>
        </p>
        <p className="mt-1 text-sm text-fg-primary font-medium" data-testid={`${testidPrefix}-module-${m.id}-title`}>
          {m.order}. {m.title}
        </p>
      </div>
      <StatusIcon className={"h-4 w-4 flex-shrink-0 " + statusClass} aria-hidden="true" />
    </Link>
  );
}

function MappingRibbon({ mapping, pathways, activeEnrollment }) {
  const recPw = pathways.find((p) => p.id === mapping.recommended_pathway_id);
  const winner = (mapping.pathways || []).find((p) => p.pathway_id === mapping.recommended_pathway_id);
  const alternatives = (mapping.pathways || []).filter((p) => p.pathway_id !== mapping.recommended_pathway_id);
  const bucket = winner?.bucket || "moderate";
  const bucketLabel = { strong: "Strong learning match", moderate: "Reasonable learning match", limited: "Partial learning match" }[bucket];
  const bucketClass = bucket === "strong"
    ? "border-progress-teal/40 bg-progress-teal/10 text-progress-teal"
    : bucket === "moderate"
    ? "border-accent-warm/40 bg-accent-warm/10 text-accent-warm"
    : "border-fg-secondary/40 bg-bg-surface-hover text-fg-secondary";

  return (
    <div
      data-testid="pathway-mapping-ribbon"
      data-recommended={mapping.recommended_pathway_id}
      data-ai-assisted={mapping.ai_assisted || false}
      className="mt-4 rounded-lg border border-progress-teal/40 bg-progress-teal/5 p-5"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <span data-testid="pathway-mapping-bucket" className={"text-[10px] uppercase tracking-wider px-2 py-1 rounded border " + bucketClass}>
            {bucketLabel}
          </span>
          {mapping.ai_assisted && (
            <span data-testid="pathway-mapping-ai-assisted-badge" className="ml-2 text-[10px] uppercase tracking-wider px-2 py-1 rounded border border-fg-muted/40 bg-bg-surface-hover text-fg-secondary">
              AI-assisted tie-break
            </span>
          )}
        </div>
        <p className="text-[11px] text-fg-muted">Based on your confirmed Lane of Direction</p>
      </div>
      <p data-testid="pathway-mapping-headline" className="mt-2 text-sm text-fg-primary">
        <span className="text-fg-primary font-medium">{recPw?.title || mapping.recommended_pathway_id}</span>
        {" "}looks like a strong learning match for your selected direction — you are still free to choose any pathway.
      </p>
      {winner?.why && (
        <p data-testid="pathway-mapping-why" className="mt-2 text-xs text-fg-secondary">
          Why it matches: {winner.why}
        </p>
      )}
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
        <div data-testid="pathway-mapping-addresses">
          <p className="text-fg-primary font-medium">Main gaps this pathway addresses</p>
          {(winner?.addresses_gaps || []).length === 0 ? (
            <p className="mt-1 text-fg-muted italic">None of your current gap items are addressed by this pathway.</p>
          ) : (
            <ul className="mt-1 space-y-0.5 text-fg-secondary">
              {(winner?.addresses_gaps || []).map((t, i) => (
                <li key={t}>· {t.replace(/_/g, " ")}</li>
              ))}
            </ul>
          )}
        </div>
        <div data-testid="pathway-mapping-unaddressed">
          <p className="text-fg-primary font-medium">Important gaps it does <em>not</em> address</p>
          {(winner?.unaddressed_gaps || []).length === 0 ? (
            <p className="mt-1 text-fg-muted italic">This pathway addresses all of your currently-mapped gap items.</p>
          ) : (
            <ul className="mt-1 space-y-0.5 text-fg-secondary">
              {(winner?.unaddressed_gaps || []).map((t) => (
                <li key={t}>· {t.replace(/_/g, " ")}</li>
              ))}
            </ul>
          )}
        </div>
      </div>
      {mapping.ai_tiebreak_why && (
        <p data-testid="pathway-mapping-ai-why" className="mt-3 text-xs text-fg-muted italic">
          AI tie-break reasoning: {mapping.ai_tiebreak_why}
        </p>
      )}
      <div className="mt-4 flex flex-wrap gap-2">
        <Link to="#pathway-cards">
          <Button size="sm" variant="ghost" data-testid="pathway-mapping-compare-btn" className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover">
            Compare all three below
          </Button>
        </Link>
        <Link to="/results#directions">
          <Button size="sm" variant="ghost" data-testid="pathway-mapping-correct-btn" className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover">
            Correct the mapping (change my direction)
          </Button>
        </Link>
      </div>
      {alternatives.length > 0 && (
        <div className="mt-3">
          <p className="text-[11px] uppercase tracking-wider text-fg-secondary">Other pathways worth considering</p>
          <ul className="mt-1 space-y-0.5 text-xs">
            {alternatives.map((p) => (
              <li key={p.pathway_id} data-testid={`pathway-mapping-alt-${p.pathway_id}`} className="text-fg-secondary">
                · <span className="text-fg-primary">{p.pathway_title}</span> — bucket <em>{p.bucket}</em>.
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}


function CapstoneEntry({ course, progress }) {  const cap = course.capstone;
  const evidence = (progress?.projects || []).find((p) => p.project_id === cap.id);
  const status = evidence?.status || "not_started";
  return (
    <section className="mt-2" data-testid="capstone-entry">
      <h2 className="text-2xl font-heading font-medium text-fg-primary flex items-center gap-2">
        <Sparkles className="h-5 w-5" aria-hidden="true" />
        Your capstone project
      </h2>
      <p className="mt-1 text-xs text-fg-secondary">
        Real work evidence — a small project that ties the lessons together.
      </p>
      <div className="mt-3 rounded-lg border border-border-subtle bg-bg-surface p-5">
        <p className="text-xs uppercase tracking-wider text-fg-secondary">
          {course.title}
        </p>
        <h3 className="mt-1 text-lg font-heading font-medium text-fg-primary" data-testid="capstone-title">{cap.title}</h3>
        <p className="mt-2 text-sm text-fg-secondary">{cap.prompt}</p>
        <p className="mt-3 text-xs text-fg-secondary" data-testid="capstone-status">
          Status: <span className="text-fg-primary">{status.replace("_", " ")}</span>
        </p>
        <Link to={`/learning/project/${cap.id}`}>
          <Button size="sm" data-testid="capstone-open-btn" className="mt-3 bg-accent-warm text-bg-base hover:bg-accent-warm-hover">
            {status === "completed" ? "View my project" : "Open the capstone"}
            <ArrowRight className="ml-2 h-3.5 w-3.5" aria-hidden="true" />
          </Button>
        </Link>
      </div>
    </section>
  );
}
